import { useStore } from "@/store";
import router from "@/router";
import { Post, PostAllInfoItem } from "@/types";
import { h, render } from "vue";
import PostBoxRowNormal from "@/components/BlogComponents/PostBoxRowNormal.vue";

interface OnTimeExeItemType {
  func: () => void;
  interval: ReturnType<typeof setInterval>;
}
// 这个锁会将整个js线程阻塞，导致页面卡死
// class BlockingMutex {
//   lokeID: number;

//   constructor(lokeID: number) {
//     this.lokeID = lokeID;
//   }

//   // while 循环阻塞直至锁被释放可被获取
//   lock() {
//     while (funcs.globalLockers[this.lokeID]);
//     funcs.globalLockers[this.lokeID] = true;
//   }

//   // 释放锁
//   unlock() {
//     funcs.globalLockers[this.lokeID] = false;
//   }
// }
// 异步锁，可以阻塞某个线程（比如按顺序请求）
// 使用例：const mutex = new AsyncMutex(); await mutex.lock(); // 锁定，执行某些操作 mutex.unlock(); // 解锁
class AsyncMutex {
  private locked = false;
  private waitingQueue: Array<() => void> = [];

  async lock(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.locked) {
        this.locked = true;
        resolve();
      } else {
        this.waitingQueue.push(resolve);
      }
    });
  }

  unlock(): void {
    if (this.waitingQueue.length > 0) {
      const nextResolve = this.waitingQueue.shift()!;
      nextResolve();
    } else {
      this.locked = false;
    }
  }
}

const funcs = {
  jumpto: (url: string) => {
    window.open(url);
  },
  jumptoLocal: (url: string) => {
    // window.location.href = url;
    router.push(url);
  },
  jumpToLocalWithoutRouter: (url: string) => {
    window.location.href = url;
  },
  reflash: () => {
    router.go(0);
  },
  reflashByWindow: () => {
    window.location.reload();
  },
  toTop: () => {
    window.scrollTo(0, 0);
  },
  switchDarkTheme: () => {
    const store = useStore();
    store.switchDarkTheme();
  },
  formatNumber: (num: number) => {
    if (num < 1000) {
      return num.toString(); // 返回原始数字
    } else {
      const k = num / 1000; // 将数字转换为千
      return k % 1 === 0 ? `${Math.floor(k)}k` : `${k.toFixed(2)}k`; // 根据是否为整数决定格式
    }
  },
  formatDatetime: (datetime: Date | string) => {
    let date = new Date(datetime);
    if (isNaN(date.getTime())) {
      return "无效日期格式";
    }
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1; // 月份从0开始
    const day = date.getUTCDate();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const seconds = date.getUTCSeconds();
    return `${year}年${month}月${day}日${hours}时${minutes}分${seconds}秒`;
  },
  countHowManyTimesAgo: (date: string) => {
    const now = new Date();
    const diffInSeconds = Math.floor(
      (now.getTime() - new Date(date.replace("Z", "")).getTime()) / 1000
    );

    if (diffInSeconds < 60) {
      return `${diffInSeconds}秒前`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}分钟前`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}小时前`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}天前`;
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months}个月前`;
    } else {
      const years = Math.floor(diffInSeconds / 31536000);
      return `${years}年前`;
    }
  },
  generatePostRefBox: (postinfo: Post) => {
    const postRefBox = h(PostBoxRowNormal, {
      propPost: {
        ...postinfo,
        is_delete: false,
        is_has_draft: false,
      } as PostAllInfoItem,
    });
    const refPostContainerDiv = document.createElement("div");
    refPostContainerDiv.classList.add("flex-center");
    refPostContainerDiv.style.margin = "2.5rem 0";
    // refPostContainerDiv.style.boxSizing = "border-box";
    render(postRefBox, refPostContainerDiv);
    return (
      refPostContainerDiv.outerHTML ||
      `<div style="font-size: 36px;font-weight: bolder;">啊哦，文章 id ${postinfo.id} 引用渲染失败</div>`
    );
  },
  // ----------------- 全局定时器（以下） -----------------
  // 用于每固定时间段执行一次的函数
  globalTimerFunctionsGroup: [] as {
    func: () => void;
    perHowManyMinToExec: number;
  }[],
  addGlobalTimerFunction: (func: () => void, perHowManyMinToExec: number) => {
    funcs.globalTimerFunctionsGroup.push({ func, perHowManyMinToExec });
  },
  removeGlobalTimerFunction: (func: () => void) => {
    for (let i = 0; i < funcs.globalTimerFunctionsGroup.length; i++) {
      if (funcs.globalTimerFunctionsGroup[i].func === func) {
        funcs.globalTimerFunctionsGroup.splice(i, 1);
        break;
      }
    }
  },
  globalTimerExecedTime: 0,
  // ----------------- 全局定时器（以上） -----------------
  // 为什么我当初没有用 ts （泪
  // 现在用上了（喜
  // 用于特定时间点执行的函数
  // 使用例：tools.addOnTimeExecute(midnightTalk, "0");
  // tools.addOnTimeExecute(morningTalk, "8:30");
  onTimeExecuteIntervalList: [] as OnTimeExeItemType[], // {func: Function, interval: number(setInterval 返回的 id)}
  getOnTimeExecute: (
    func: () => void,
    intervalID: ReturnType<typeof setInterval>
  ): OnTimeExeItemType | null => {
    funcs.onTimeExecuteIntervalList.forEach((intervalItem) => {
      if (intervalItem.func === func && intervalItem.interval === intervalID) {
        return intervalItem;
      }
    });
    return null;
  },
  getOnTimeExecuteByFunc: (func: () => void) => {
    for (const intervalItem of funcs.onTimeExecuteIntervalList) {
      if (intervalItem.func === func) {
        return intervalItem;
      }
    }
  },
  addOnTimeExecute: (func: () => void, time: string | Date) => {
    let intervalFirstID = null;
    const now = new Date();
    let targetTime = new Date(now);
    if (typeof time === "string" && !time.includes(" ")) {
      // 假定输入为 "HH:mm:ss" 或 "HH:mm" 或仅有小时 格式的字符串
      const [hours, minutes = 0, seconds = 0] = time.split(":").map(Number);
      targetTime.setHours(hours, minutes, seconds, 0);
    } else if (time instanceof Date) {
      // 如果输入已经是 Date，只使用其时间部分
      targetTime.setHours(
        time.getHours(),
        time.getMinutes(),
        time.getSeconds(),
        time.getMilliseconds()
      );
    } else {
      // 其他可能被转为 Date 的内容
      targetTime = new Date(time);
      if (isNaN(targetTime.getTime())) {
        throw new Error("Invalid input: Unable to parse as a valid time.");
      }
      targetTime.setFullYear(now.getFullYear(), now.getMonth(), now.getDate());
    }
    // 如果目标时间已经过去，将其调整到第二天
    if (targetTime.getTime() < now.getTime()) {
      targetTime.setDate(targetTime.getDate() + 1); // 日期加一天
    }

    // 计算时间差
    const timeDifference = targetTime.getTime() - now.getTime();
    (function setOnTimeExecute() {
      let isFirstExecute = true;
      let id = setInterval(() => {
        if (isFirstExecute) {
          func();
          isFirstExecute = false;
        } else {
          clearInterval(id);
          let newId = setInterval(() => {
            func();
          }, 24 * 60 * 60 * 1000);
          let intervalItem = funcs.getOnTimeExecute(func, id);
          if (intervalItem) {
            intervalItem.interval = newId;
          }
        }
      }, timeDifference);
      funcs.onTimeExecuteIntervalList.push({ func, interval: id });
      intervalFirstID = id;
    })();
    return intervalFirstID;
  },
  removeOnTimeExecute: (func: () => void) => {
    let intervalItem = funcs.getOnTimeExecuteByFunc(func);
    if (!intervalItem) return;
    clearTimeout(intervalItem.interval);
    funcs.onTimeExecuteIntervalList.splice(
      funcs.onTimeExecuteIntervalList.indexOf(intervalItem),
      1
    );
  },
  // ----------------- 以上包括内容不行就删掉 -----------------
  newMutex: () => {
    return new AsyncMutex();
  },
};

// 全局定时器 Timer
setInterval(() => {
  funcs.globalTimerExecedTime++;
  funcs.globalTimerFunctionsGroup.forEach((element) => {
    if (funcs.globalTimerExecedTime % element.perHowManyMinToExec === 0) {
      element.func();
    }
  });
}, 60000); // 1分钟

export default funcs;
